// The data rules behind the gateway settings form's Form mode, kept out of the
// component for the same reason jsonObjectValue.js and scraperSettings.js are: they
// rewrite a settings row's live settings, so they are pure functions with tests
// rather than logic inside a React class jest cannot import (src/i18n.js awaits at
// the top level).
//
// The cabinet serves the settings of each driver, one per gateway API and kind, at
// /api/public-gateway-settings/schemas, as core describes them from the driver's
// settings class:
//
//     [{ api: 'xscraper', kind: 'payout', fields: [field, …] }, …]
//
//     field = { key, type, format?, required, secret, deprecated, defaultValue?,
//               options?, min?, max?, fields?, item?, widget?, description? }
//
// type is text | integer | number | boolean | duration | choice | object | list |
// json; an object lists its keys in `fields`, a list describes its elements in `item`
// (a field without a key). format is url | code | regex, for text.
//
// A secret comes back masked (`tok***def`, or `***` when short), and a save that
// sends the mask back keeps the stored value — so the form leaves a mask in place
// until the operator types a new value.

const isPlainObject = (value) =>
    value !== null && typeof value === 'object' && !Array.isArray(value);

/** What the cabinet sends for a stored secret: `***`, or three characters either side of it. */
export const isMask = (value) =>
    typeof value === 'string' && /^(\*\*\*|[\s\S]{3}\*\*\*[\s\S]{3})$/.test(value);

/**
 * The schema of the `kind` driver of the gateway API `gatewayApiId`, or null: no
 * kind or API chosen, an API no driver of that kind answers to, or the schemas not
 * loaded. `apis` are the gateway APIs' light rows, which name them.
 */
export const schemaFor = (kind, gatewayApiId, apis = [], schemas = []) => {
    if (!kind || gatewayApiId === null || gatewayApiId === undefined || gatewayApiId === '') return null;
    const api = (apis || []).find((item) => String(item.id) === String(gatewayApiId));
    if (!api) return null;
    return (schemas || []).find((schema) => schema.api === api.name && schema.kind === kind) || null;
};

/** Whether the gateway API `api` (a light row, with its `kinds`) may back settings of `kind`; any may before a kind is chosen. */
export const apiServesKind = (api, kind) => !kind || (api?.kinds || []).includes(kind);

/**
 * The one link a settings row of each gateway API reads beside its settings: the
 * bot a tg_bot row posts through, the chat a tg_session row reads, the proxies an
 * xscraper row's browser goes out through. Other APIs read none.
 */
export const SETTINGS_LINKS = { tg_bot: 'tgBotId', tg_session: 'tgSessionChatId', xscraper: 'proxyListId' };

/**
 * Whether the settings form shows the link `linkField` for the API `gatewayApiId`:
 * not before an API is chosen, and only for the API that reads it. An API missing
 * from `apis` — not loaded yet, or archived — shows every link, since a hidden one
 * is cleared on the next change and would unlink what is stored.
 */
export const showsLink = (linkField, gatewayApiId, apis = []) => {
    if (gatewayApiId === null || gatewayApiId === undefined || gatewayApiId === '') return false;
    const api = (apis || []).find((item) => String(item.id) === String(gatewayApiId));
    if (!api) return true;
    return SETTINGS_LINKS[api.name] === linkField;
};

/** The value at `path` (keys and list indexes), or undefined. */
export const getAt = (value, path) =>
    path.reduce((current, step) => (current === null || current === undefined ? undefined : current[step]), value);

/**
 * `value` with `next` at `path`, every other key and element the same value as
 * before. `undefined` removes the key — an absent key is what lets the driver's
 * default apply — and, in a list, the element.
 */
export const setAt = (value, path, next) => {
    if (path.length === 0) return next;
    const [step, ...rest] = path;
    if (typeof step === 'number') {
        const list = Array.isArray(value) ? [...value] : [];
        const child = setAt(list[step], rest, next);
        if (child === undefined && rest.length === 0) {
            list.splice(step, 1);
        } else {
            list[step] = child;
        }
        return list;
    }
    const object = isPlainObject(value) ? { ...value } : {};
    const child = setAt(object[step], rest, next);
    if (child === undefined) {
        delete object[step];
    } else {
        object[step] = child;
    }
    return object;
};

/** The dotted path the cabinet names a problem by: `incoming[0].pattern`. */
export const pathText = (path) =>
    path.reduce((text, step) => (typeof step === 'number' ? `${text}[${step}]` : text ? `${text}.${step}` : step), '');

const TEXTUAL = new Set(['text', 'choice', 'duration']);

/** Absent, null, or blank where a cleared input posts "" — not for a number, which Jackson would read as 0. */
export const isAbsent = (field, value) =>
    value === undefined ||
    value === null ||
    (TEXTUAL.has(field.type) && typeof value === 'string' && value.trim() === '');

// Java's Duration.parse: days, hours, minutes, seconds, each optionally signed.
const ISO_DURATION = /^[-+]?P(?:[-+]?\d+D)?(?:T(?:[-+]?\d+H)?(?:[-+]?\d+M)?(?:[-+]?\d+(?:[.,]\d{0,9})?S)?)?$/i;

export const isIsoDuration = (text) =>
    typeof text === 'string' && ISO_DURATION.test(text.trim()) && !/^[-+]?PT?$/i.test(text.trim());

/** `PT1H30M` as `1h 30m`; '' for anything it cannot read. */
export const humanDuration = (value) => {
    if (typeof value === 'number') return humanDuration(`PT${value}S`);
    if (!isIsoDuration(value)) return '';
    const match = value.trim().toUpperCase().match(/^([-+]?)P(?:([-+]?\d+)D)?(?:T(?:([-+]?\d+)H)?(?:([-+]?\d+)M)?(?:([-+]?\d+(?:[.,]\d+)?)S)?)?$/);
    if (!match) return '';
    const parts = [
        [match[2], 'd'],
        [match[3], 'h'],
        [match[4], 'm'],
        [match[5], 's']
    ].filter(([amount]) => amount !== undefined && Number(String(amount).replace(',', '.')) !== 0);
    if (parts.length === 0) return '0s';
    return `${match[1] === '-' ? '-' : ''}${parts.map(([amount, unit]) => `${String(amount).replace(',', '.')}${unit}`).join(' ')}`;
};

const isHttpUrl = (text) => /^https?:\/\/[^\s/?#:]+/i.test(text.trim());

const problem = (path, key, params = {}) => ({ path: pathText(path), key, params: { path: pathText(path), ...params } });

/**
 * What the cabinet will refuse in `value` for this schema, as `[{ path, key, params }]`
 * with `key` an i18n key: the same rules as its GatewaySettingsCheck, so Save is held
 * for what the save would be refused for. Two are left to the cabinet: whether a
 * regex compiles — it is Java's regex, not the browser's — and nothing else is
 * guessed at. Keys the driver does not read are not problems.
 */
export const settingsProblems = (schema, value) => {
    const problems = [];
    if (!schema) return problems;
    if (value !== null && value !== undefined && !isPlainObject(value)) {
        return [problem([], 'gatewaySettings.errors.notObject')];
    }
    checkFields(schema.fields, value, [], problems);
    return problems;
};

function checkFields(fields, object, prefix, problems) {
    (fields || []).forEach((field) => {
        const value = isPlainObject(object) ? object[field.key] : undefined;
        checkValue(field, value, [...prefix, field.key], problems);
    });
}

function checkValue(field, value, path, problems) {
    if (isAbsent(field, value)) {
        if (field.required && !field.deprecated) problems.push(problem(path, 'gatewaySettings.errors.required'));
        return;
    }
    if (field.deprecated) {
        problems.push(problem(path, 'gatewaySettings.errors.deprecated'));
        return;
    }
    switch (field.type) {
        case 'text':
            if (typeof value !== 'string') {
                problems.push(problem(path, 'gatewaySettings.errors.text'));
            } else if (field.format === 'url' && !isHttpUrl(value)) {
                problems.push(problem(path, 'gatewaySettings.errors.url'));
            }
            break;
        case 'choice':
            if (!(field.options || []).includes(value)) {
                problems.push(problem(path, 'gatewaySettings.errors.choice', { options: (field.options || []).join(', ') }));
            }
            break;
        case 'integer':
            if (!Number.isInteger(value)) {
                problems.push(problem(path, 'gatewaySettings.errors.integer'));
            } else {
                checkBounds(field, value, path, problems);
            }
            break;
        case 'number':
            if (typeof value !== 'number' || !Number.isFinite(value)) {
                problems.push(problem(path, 'gatewaySettings.errors.number'));
            } else {
                checkBounds(field, value, path, problems);
            }
            break;
        case 'boolean':
            if (typeof value !== 'boolean') problems.push(problem(path, 'gatewaySettings.errors.boolean'));
            break;
        case 'duration':
            if (typeof value !== 'number' && !isIsoDuration(value)) {
                problems.push(problem(path, 'gatewaySettings.errors.duration'));
            }
            break;
        case 'object':
            if (!isPlainObject(value)) {
                problems.push(problem(path, 'gatewaySettings.errors.object'));
            } else {
                checkFields(field.fields, value, path, problems);
            }
            break;
        case 'list':
            checkList(field, value, path, problems);
            break;
        case 'json':
            if (!isPlainObject(value)) problems.push(problem(path, 'gatewaySettings.errors.json'));
            break;
        default:
            break;
    }
}

function checkList(field, value, path, problems) {
    if (!Array.isArray(value)) {
        problems.push(problem(path, 'gatewaySettings.errors.list'));
        return;
    }
    if (value.length === 0) {
        if (field.required) problems.push(problem(path, 'gatewaySettings.errors.listEmpty'));
        return;
    }
    if (!field.item) return;
    value.forEach((item, index) => {
        if (item === null || item === undefined) {
            problems.push(problem([...path, index], 'gatewaySettings.errors.empty'));
        } else {
            checkValue(field.item, item, [...path, index], problems);
        }
    });
}

function checkBounds(field, value, path, problems) {
    if (field.min !== undefined && field.min !== null && value < field.min) {
        problems.push(problem(path, 'gatewaySettings.errors.min', { min: field.min }));
    } else if (field.max !== undefined && field.max !== null && value > field.max) {
        problems.push(problem(path, 'gatewaySettings.errors.max', { max: field.max }));
    }
}

/** Top-level keys of `value` the driver does not read: kept, and shown so they can be removed. */
export const unknownKeys = (schema, value) => {
    if (!schema || !isPlainObject(value)) return [];
    const known = new Set((schema.fields || []).map((field) => field.key));
    return Object.keys(value).filter((key) => !known.has(key));
};

/** Top-level keys the driver refuses now (deprecated) that `value` still carries. */
export const deprecatedKeys = (schema, value) => {
    if (!schema || !isPlainObject(value)) return [];
    return (schema.fields || [])
        .filter((field) => field.deprecated && value[field.key] !== undefined && value[field.key] !== null)
        .map((field) => field.key);
};

/** `value` without `keys`. */
export const withoutKeys = (value, keys) => {
    if (!isPlainObject(value)) return value;
    const out = { ...value };
    keys.forEach((key) => delete out[key]);
    return out;
};

/**
 * What an integer input's text becomes: a number, `undefined` when blank (the key
 * is removed and the default applies), or the text itself when it is not a whole
 * number — kept so the input shows what was typed, and refused by settingsProblems.
 */
export const parseInteger = (text) => {
    const trimmed = String(text).trim();
    if (trimmed === '') return undefined;
    return /^[-+]?\d+$/.test(trimmed) && Number.isSafeInteger(Number(trimmed)) ? Number(trimmed) : trimmed;
};

export const parseNumber = (text) => {
    const trimmed = String(text).trim();
    if (trimmed === '') return undefined;
    const number = Number(trimmed.replace(',', '.'));
    return Number.isFinite(number) ? number : trimmed;
};

/** A new list element: an object with nothing set, or nothing at all. */
export const emptyItem = (item) => (item?.type === 'object' ? {} : undefined);

/** Problems keyed by their path text, the first for each path. */
export const problemsByPath = (problems) =>
    problems.reduce((byPath, item) => (byPath[item.path] ? byPath : { ...byPath, [item.path]: item }), {});
